/**
 * features/landing/work.ts
 * ------------------------------------------------------------------
 * All marketing copy for the public company page lives here so the
 * page component stays layout-only and the wording can be edited
 * without touching JSX.
 *
 * Screenshots: each project looks for `/work/<image>` in `public/`.
 * Until a file exists the frame renders an on-brand abstract mock, so
 * the page is presentable today and upgrades the moment a real
 * screenshot is dropped in. Recommended: 1600×1000 PNG/JPG (16:10).
 * ------------------------------------------------------------------
 */
import {
  Code2,
  GraduationCap,
  Receipt,
  Smartphone,
  Search,
  PenTool,
  Hammer,
  LifeBuoy,
  type LucideIcon,
} from 'lucide-react';

/* ============================================================ */
/* Company facts                                                 */
/* ============================================================ */

export const COMPANY = {
  /** The REGISTERED name, exactly as on the CIPA certificate of incorporation
   *  (UIN BW00009613165, 10 March 2026). Meta's business verification and any
   *  other check against the certificate matches this string exactly — do not
   *  restyle it. */
  legalName: 'Acepsle Proprietary Limited',
  /** How the company presents itself in marketing copy and sign-offs. */
  tradingName: 'AcePSLE (PTY) Ltd',
  displayName: 'Acepsle',
  tagline: 'Software development · Gaborone, Botswana',
  email: 'info@acepslebotswana.com',
  phone: '+267 73 453 092',
  phoneAlt: '+267 75 470 389',
  phoneHref: '+26773453092',
  address: 'Plot 37043 Block 8, Gaborone, Botswana',
  registration: 'BW00009613165',
  incorporated: '2026-03-10',
  site: 'https://acepslebotswana.com',
} as const;

/* ============================================================ */
/* What we do                                                    */
/* ============================================================ */

export interface Service {
  icon: LucideIcon;
  title: string;
  body: string;
}

export const SERVICES: Service[] = [
  {
    icon: Code2,
    title: 'Custom platforms',
    body: 'Web and mobile systems designed around how your organisation actually works — not a template bent to fit it. Built end to end, in-house.',
  },
  {
    icon: GraduationCap,
    title: 'School management',
    body: 'School Portal — our own platform for admissions, academics, attendance, reports, parent portals, HR, payroll and full accounting. Running daily in Botswana schools today.',
  },
  {
    icon: Receipt,
    title: 'Business & finance systems',
    body: 'Invoicing, payments, statements, expenses and compliance — structured for BURS requirements and Botswana’s move to electronic VAT invoicing.',
  },
  {
    icon: Smartphone,
    title: 'Product design & PWAs',
    body: 'Installable, offline-capable web apps that feel native on any phone. No app store, no downloads, no expensive hardware.',
  },
];

/* ============================================================ */
/* Selected work                                                 */
/* ============================================================ */

export interface Project {
  /**
   * Stable slug. Doubles as the image-slot id in the admin media
   * library, so adding a project here adds its upload slot there.
   * Changing it orphans any already-uploaded screenshot.
   */
  id: string;
  /** Section eyebrow — sector / client type. */
  sector: string;
  name: string;
  /** One-line positioning under the name. */
  summary: string;
  /** Two or three sentences of substance. */
  body: string;
  /** Short proof points rendered as chips. */
  chips: string[];
  /** Live URL. Empty string renders no link. */
  href: string;
  /**
   * Link text. Defaults to "Visit <domain>". Set this where the link
   * goes somewhere better than the front door — a feature tour rather
   * than a login screen, which is all a visitor would otherwise see.
   */
  linkLabel?: string;
  /** Shown in the browser-frame URL bar. */
  domain: string;
  /**
   * Fallback screenshot filename inside `public/work/`. Only used when
   * no image has been uploaded for this slot in the media library.
   */
  image: string;
  /** Accent colour for the fallback mock. */
  accent: string;
  /** 'client' work vs an Acepsle-owned 'product'. */
  kind: 'client' | 'product';
}

export const PROJECTS: Project[] = [
  {
    id: 'lkc',
    sector: 'Education · School Portal deployment',
    name: 'LKC Portal',
    summary: 'School Portal at Livingstone Kolobeng College',
    body: 'Our School Portal platform, deployed for a secondary school and covering the whole of it: admissions, academics, marks, comments and report generation, attendance, discipline, curriculum, and a parent portal. Behind it sits full HR and payroll with Botswana PAYE and BURS statutory forms, plus finance for fees, billing and statements. It runs daily in production for hundreds of students and staff.',
    chips: ['Live in production', 'Botswana PAYE & BURS', 'Parent portal', 'Fees & billing'],
    href: '/platforms/lkc-portal.html',
    linkLabel: 'Take the LKC platform tour',
    domain: 'lkcportal.com',
    image: 'lkc.png',
    accent: '#2563EB',
    kind: 'client',
  },
  {
    id: 'alnur',
    sector: 'Education · School Portal deployment',
    name: 'Al-Nur Portal',
    summary: 'School Portal at Al-Nur School, with full accounting',
    body: 'The same School Portal platform shaped to a different school, and taken further. Alongside admissions, assessment from Reception upwards, Cambridge-format reports, attendance and a parent portal, Al-Nur runs a complete set of books in the same system — chart of accounts, journals, trial balance, income and expenditure, balance sheet, fixed assets and suppliers — plus till and stock control for uniform and the tuck shop. One system instead of the spreadsheets, files and WhatsApp threads it replaced.',
    chips: ['Full accounting suite', 'Stock & till', 'Cambridge reports', 'HR & payroll'],
    href: '/platforms/alnur-portal.html',
    linkLabel: 'Take the Al-Nur platform tour',
    domain: 'alnurportal.com',
    image: 'alnur.png',
    accent: '#0E9AA7',
    kind: 'client',
  },
  {
    id: 'gracehaven',
    sector: 'Hospitality · Client platform',
    name: 'Grace Haven',
    summary: 'Spa, BnB and car hire — website, bookings and back office',
    body: 'A public website that sells the experience, an online booking flow behind it, and an admin portal the owners actually run the business from. Treatments, room stays and vehicle hire in one system, with invoices, receipts and gift cards generated straight from the booking.',
    chips: ['Online booking', 'Admin portal', 'Invoices & gift cards', 'Installable PWA'],
    href: 'https://gracehaven.co.bw',
    domain: 'gracehaven.co.bw',
    image: 'gracehaven.png',
    accent: '#C9962E',
    kind: 'client',
  },
  {
    id: 'wordbattles',
    sector: 'Consumer product · Acepsle',
    name: 'WordBattles',
    summary: 'Multiplayer Bible knowledge games',
    body: 'A Jackbox-style game platform for churches, schools, families and youth groups. One person hosts, everyone else joins from their own phone with a room code — then it is a real-time scramble to the buzzer across eleven games, including AI-judged verse recitation that scores what you say aloud, word by word.',
    chips: ['Real-time multiplayer', 'AI verse judging', 'Eleven games', 'No download'],
    href: 'https://wordbattles.app',
    domain: 'wordbattles.app',
    image: 'wordbattles.png',
    accent: '#7C4DFF',
    kind: 'product',
  },
  {
    id: 'acepsle',
    sector: 'Education product · Acepsle',
    name: 'AcePSLE',
    summary: 'PSLE revision for Botswana primary learners',
    body: 'Standard 5–7 pupils preparing for the PSLE, with practice built against the Botswana syllabus rather than a generic international one. Short sessions, past-paper style questions and immediate feedback — designed around how learners here actually revise, and light enough to run well on the modest phone most families already own.',
    chips: ['Standards 5–7', 'Botswana syllabus', 'Past-paper practice', 'Installable'],
    // NOTE: acepslebotswana.com serves this app until the year-end domain
    // swap, after which it moves to acepsle.co.bw — see docs/domain-plan.md.
    href: 'https://acepslebotswana.com',
    domain: 'acepslebotswana.com',
    image: 'acepsle.png',
    accent: '#F97316',
    kind: 'product',
  },
  {
    id: 'pasa7',
    sector: 'Education product · Acepsle',
    name: 'Pasa 7',
    summary: 'ZIMSEC Grade 7 revision, for Zimbabwe',
    body: 'The same revision engine taken across a border and onto another country’s exam. Pasa 7 prepares Grade 7 learners for ZIMSEC across seven subjects, each broken into the skills the syllabus actually expects — 249 of them mapped — so practice goes where a child’s gaps are rather than wherever a question bank happens to start. Every answer is marked and explained, a readiness test gives a parent a picture in about twenty-five minutes, and an account remembers what the child found hard so the next session begins there.',
    chips: ['ZIMSEC Grade 7', 'Seven subjects', 'Readiness test', 'Marked and explained'],
    href: 'https://pasa7.com',
    domain: 'pasa7.com',
    image: 'pasa7.png',
    accent: '#00A651',
    kind: 'product',
  },
  {
    id: 'motshelo',
    sector: 'Fintech product · Acepsle',
    name: 'Motshelo',
    summary: 'Books for a savings group',
    body: 'A motshelo runs on trust and a notebook. This keeps the book instead — contributions, loans at interest, and a share-out every member can check for themselves on their own phone. The decision that defines it is what it deliberately does not do: the group’s money stays wherever the group already keeps it. We hold the record, never the funds, which keeps it a software product rather than a regulated financial one.',
    chips: ['Contributions & loans', 'Auditable share-out', 'Phone sign-in', 'Record-keeping only'],
    href: 'https://motshelov1.web.app',
    domain: 'motshelov1.web.app',
    image: 'motshelo.png',
    accent: '#1F7A5A',
    kind: 'product',
  },
  {
    id: 'postm8s',
    sector: 'Property product · Acepsle',
    name: 'Postm8s',
    summary: 'White-label property management and accounting for landlords',
    body: 'A landlord’s whole operation in one system: units, tenants and leases, the rent roll, maintenance, invoicing, payments, expenses, HR and reporting. It is white-label from the ground up — brand colours, product name, estate layout and tax parameters all live in configuration files, so standing the same system up for another landlord is a config swap rather than a fork.',
    chips: ['Rent roll & leases', 'Tenants & units', 'Maintenance', 'White-label'],
    href: 'https://postm8s.web.app',
    domain: 'postm8s.web.app',
    image: 'postm8s.png',
    accent: '#6E2A33',
    kind: 'product',
  },
  {
    id: 'carsearch',
    sector: 'Automotive product · Acepsle',
    name: 'CarSearch Botswana',
    summary: 'Used-car marketplace with partner garage inspections',
    body: 'A used-car marketplace for Botswana built around evidence rather than adverts. Sellers verify their ID, staff check each car’s registration papers, and partner garages publish full inspection reports, safety checks first. Buyers save searches and get alerts, and Import for me links them with sellers in South Africa, with a full quotation of every cost in pula before they commit.',
    chips: ['Garage inspections', 'Verified sellers', 'Car alerts', 'Import for me'],
    // NOTE: moves to https://carsearch.co.bw once that domain is registered and pointed at Firebase Hosting.
    href: 'https://carsearchbw.web.app',
    domain: 'carsearchbw.web.app',
    image: 'carsearch.png',
    accent: '#0F3E6E',
    kind: 'product',
  },
  {
    id: 'hub',
    sector: 'Business product · Acepsle',
    name: 'Acepsle Hub',
    summary: 'Tax-ready business management for Botswana',
    body: 'Our own business suite, and the system this page signs you into. Invoicing, receipts, payments, quotes, credit notes, statements, expenses and a compliance register — with gapless document numbering, an append-only audit trail and VAT wired in but dormant, so BURS registration becomes a settings toggle rather than a rebuild.',
    chips: ['Gapless numbering', 'Audit trail', 'VAT-ready', 'BURS-shaped'],
    href: '',
    domain: 'acepslebotswana.com',
    image: 'hub.png',
    accent: '#FF6B35',
    kind: 'product',
  },
];

/* ============================================================ */
/* How we work                                                   */
/* ============================================================ */

/**
 * School Portal — our own product, presented as one.
 *
 * It is NOT just another portfolio entry. The LKC Portal and the Al-Nur Portal
 * are two deployments of this one product, so listing them as separate bespoke
 * builds undersold it: a school reading the portfolio saw two one-off projects
 * rather than a product already running in two schools.
 *
 * It keeps its OWN palette here — navy and green, measured from the product's
 * own material — deliberately against the Acepsle orange elsewhere on this
 * page. A product needs to look like itself, and the band reads as "a thing
 * Acepsle makes" rather than another section of the agency site.
 *
 * Wording matches the pitch exactly (tagline, scope line, "learners"), so a
 * visitor who follows the link does not meet different words for the same
 * thing. Edit both together: src/lib/templates.ts holds the email copy and
 * public/pitches/schoolportal/content.js holds the pitch copy.
 */
export const SCHOOL_PORTAL = {
  name: 'School Portal',
  tagline: 'Built to make schools work better.',
  scope: 'From the boardroom to the classroom.',
  eyebrow: 'Our product',
  headline: 'A school is not one job.',
  lede:
    'It is a principal, a board, a bursar, heads of department, class teachers, a ' +
    'registrar, parents and learners — each needing something different from the ' +
    'same information. School Portal puts every one of those roles on one platform, ' +
    'so the school stops re-entering its own data.',
  /** The four claims that hold up without a demonstration. */
  points: [
    {
      title: 'Every role, not every module',
      body:
        'Eighteen roles, from governance and audit to teaching, admissions, HR and ' +
        'payroll — each with the part of the system that is actually theirs.',
    },
    {
      title: 'One record, entered once',
      body:
        'A mark, a fee or an address is captured by the person closest to it, and ' +
        'the register, the report and the invoice all read from there.',
    },
    {
      title: 'Already running in schools',
      body:
        'In daily production in secondary schools — admissions through to report ' +
        'cards, fees, and a full set of books in one case.',
    },
    {
      /*
       * Country-neutral on purpose: Acepsle is in Gaborone and says so all over
       * this page, but the PRODUCT is not "for Botswana" — it is sold beyond it.
       */
      title: 'Built and supported by the people who wrote it',
      body:
        'By people who have taught in schools, and who answer the telephone ' +
        'themselves. Installable on any phone, and it works offline.',
    },
  ],
  /** The role-by-role presentation. */
  pitchHref: '/pitches/schoolportal/',
  pitchLabel: 'See the role-by-role presentation',
  /** Deployments, named as proof rather than as separate projects. */
  deployments: [
    { school: 'Livingstone Kolobeng College', href: '/platforms/lkc-portal.html', label: 'LKC Portal' },
    { school: 'Al-Nur School', href: '/platforms/alnur-portal.html', label: 'Al-Nur Portal' },
  ],
  /** Product palette — kept out of tailwind.config.js, which is Acepsle's. */
  colors: { navy: '#002F90', navyInk: '#001F66', green: '#2CAA34', pale: '#D9ECFE' },
};

export interface Step {
  icon: LucideIcon;
  step: string;
  title: string;
  body: string;
}

export const APPROACH: Step[] = [
  {
    icon: Search,
    step: '01',
    title: 'Understand the work',
    body: 'We start with how your organisation runs today — the forms, the spreadsheets, the workarounds people have invented. That is the real specification.',
  },
  {
    icon: PenTool,
    step: '02',
    title: 'Design in the open',
    body: 'You see working screens early, not slide decks. Direction gets corrected while it is still cheap to correct.',
  },
  {
    icon: Hammer,
    step: '03',
    title: 'Build it properly',
    body: 'Typed, tested against real data, and structured so the next feature does not require a rewrite. Compliance and audit are built in from the first commit, never bolted on.',
  },
  {
    icon: LifeBuoy,
    step: '04',
    title: 'Stay with it',
    body: 'Launch is the start. We maintain, extend and support the systems we build — and you can reach the people who wrote the code.',
  },
];

/* ============================================================ */
/* Stats + stack                                                 */
/* ============================================================ */

export interface Stat {
  value: number;
  suffix?: string;
  label: string;
}

export const STATS: Stat[] = [
  { value: 10, label: 'Platforms designed and built' },
  { value: 3, label: 'Client organisations served' },
  { value: 100, suffix: '%', label: 'Built and supported in Botswana' },
];

export const STACK = [
  'React',
  'TypeScript',
  'Firebase',
  'Cloud Functions',
  'Progressive Web Apps',
  'Tailwind',
  'jsPDF',
];

/* ============================================================ */
/* Why us                                                        */
/* ============================================================ */

export const WHY: Array<{ title: string; body: string }> = [
  {
    title: 'Local context, not localisation',
    body: 'Pula, PAYE, BURS, the school terms and the way business is actually done here — designed in from the start, not translated in afterwards.',
  },
  {
    title: 'One team, end to end',
    body: 'The people who design your system are the people who build it and the people who answer when you call. Nothing is outsourced.',
  },
  {
    title: 'You own your data',
    body: 'Your records stay yours — exportable, readable, never held hostage. No lock-in dressed up as a feature.',
  },
  {
    title: 'Built to be lived in',
    body: 'These are systems people use every working day. They are fast on a modest phone, forgiving of mistakes, and quiet about their own cleverness.',
  },
];
